'use client';

import React, { useState } from 'react';

const domains = [
  {
    name: 'AL-HAFIZ',
    meaning: 'The Preserver',
    domain: 'HOUSING & SHELTER',
    desc: 'Housing is not just shelter — it is the preservation of dignity, family, and the conditions for worship. Every roof is a sign of Al-Hafiz.',
    channelLine: 'Each housing resource is a channel through which Al-Hafiz (The Preserver) operates in this ward.',
    items: ['Emergency housing', 'Affordable housing advocacy', 'Tenant rights', 'Transitional housing', 'Home ownership pathways'],
    vocab: 'REHABILITATION',
  },
  {
    name: 'AL-ALIM',
    meaning: 'The All-Knowing',
    domain: 'EDUCATION & ILM',
    desc: 'Education is a channel of the All-Knowing. Every school, every teacher, every book is an Ayah of Al-Alim — knowledge flowing from its source.',
    channelLine: 'Each education resource is a channel through which Al-Alim (The All-Knowing) is made accessible here.',
    items: ['Islamic schools', 'Homeschool networks', 'Adult education', 'Quran programs', 'Scholarship funds'],
    vocab: 'ILM',
  },
  {
    name: 'AR-RAZZAQ',
    meaning: 'The Ever-Provider',
    domain: 'FOOD & PROVISION',
    desc: 'Food banks, halal food access, community gardens — all are expressions of Ar-Razzaq\'s provision flowing through human hands.',
    channelLine: 'Each food resource is a channel through which Ar-Razzaq (The Ever-Provider) flows in this community.',
    items: ['Food banks', 'Halal food access', 'Community gardens', 'Meal programs', 'Nutrition education'],
    vocab: 'WORK',
  },
  {
    name: 'AS-SALAM',
    meaning: 'The Source of Peace',
    domain: 'MENTAL HEALTH & WELLBEING',
    desc: 'Peace is not the absence of difficulty — it is the presence of As-Salam. Mental health resources are tools for restoring the conditions of inner peace.',
    channelLine: 'Each mental health resource is a channel through which As-Salam (The Source of Peace) reaches this ward.',
    items: ['Muslim counselors', 'Crisis support', 'Community circles', 'Grief support', 'Youth programs'],
    vocab: 'TAWBA',
  },
  {
    name: 'AL-ADL',
    meaning: 'The Just',
    domain: 'LEGAL & RIGHTS',
    desc: 'Justice is not a human invention — it is a Name of Allah. Legal aid, civil rights, advocacy — all are expressions of Al-Adl working through caretakers.',
    channelLine: 'Each legal resource is a channel through which Al-Adl (The Just) moves in this city.',
    items: ['Legal aid', 'Civil rights advocacy', 'Immigration support', 'Prisoner support', 'Anti-discrimination'],
    vocab: 'FURQAN',
  },
  {
    name: 'AL-MUHAYMIN',
    meaning: 'The Guardian',
    domain: 'COMMUNITY SAFETY',
    desc: 'The Guardian watches over. Community safety programs, conflict resolution, and neighborhood care are expressions of Al-Muhaymin through human agency.',
    channelLine: 'Each safety resource is a channel through which Al-Muhaymin (The Guardian) protects this community.',
    items: ['Neighborhood watch', 'Conflict resolution', 'Youth mentorship', 'Anti-violence programs', 'Community policing alternatives'],
    vocab: 'CARETAKERS',
  },
];

export default function CaretakingDomains() {
  const [active, setActive] = useState(0);

  return (
    <section className="py-24 bg-black border-t border-white/10" id="domains">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-8">

        <div className="text-center mb-12">
          <h2 className="font-mono text-3xl font-black text-white uppercase tracking-tight mb-4">Caretaking Domains</h2>
          <p className="font-mono text-xs text-white/30 max-w-xl mx-auto">
            Each domain is framed by a Name of Allah — not as metaphor but as recognition of the divine attribute being expressed through human service.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">

          {/* Domain selector */}
          <div className="flex flex-col gap-1 lg:w-64 flex-shrink-0">
            {domains?.map((d, i) => (
              <button
                key={d?.domain}
                onClick={() => setActive(i)}
                className={`text-left p-4 border transition-all duration-300 ${
                  active === i ? 'border-white bg-white/5' : 'border-white/10 hover:border-white/30'
                }`}
              >
                <p className={`font-mono text-xs font-black tracking-widest uppercase ${
                  active === i ? 'text-white' : 'text-white/40'
                }`}>{d?.name}</p>
                <p className="font-mono text-xs text-white/20 mt-0.5">{d?.domain}</p>
              </button>
            ))}
          </div>

          {/* Active domain */}
          <div className="flex-1">
            <div className="border border-white/10 p-8 lg:p-10">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <span className="font-mono text-xs tracking-widest text-white/20 border border-white/10 px-2 py-1 mb-3 inline-block">{domains?.[active]?.vocab}</span>
                  <h3 className="font-mono text-2xl font-black text-white uppercase tracking-tight">{domains?.[active]?.domain}</h3>
                  <p className="font-mono text-sm text-white/40 mt-1">{domains?.[active]?.name} — {domains?.[active]?.meaning}</p>
                </div>
              </div>

              {/* Names-as-channels framing line */}
              <p className="font-mono text-xs text-white/30 leading-relaxed mb-6 italic border-l-2 border-white/10 pl-4">
                {domains?.[active]?.channelLine}
              </p>

              <p className="font-mono text-sm text-white/50 leading-relaxed mb-8">{domains?.[active]?.desc}</p>

              <div>
                <p className="font-mono text-xs text-white/20 uppercase tracking-widest mb-4">Resources in this domain:</p>
                <div className="flex flex-wrap gap-2">
                  {domains?.[active]?.items?.map((item) => (
                    <span key={item} className="font-mono text-xs text-white/40 border border-white/10 px-3 py-2 hover:border-white/30 hover:text-white/60 transition-colors cursor-pointer">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
